import React, { FC, useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { matchPath, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { SubPageModel } from '../../../common/model/ui/sub-page.model';
import { getMobileNavTabs } from '../../../common/utils/services/mobile-nav-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { useRouteUrl } from '../../../hooks/use-route-url';

const MORE_TAB = 'more';

const OvMobileNav: FC<{ subPages: SubPageModel[] }> = ({ subPages }) => {
  const { t } = useTranslation();
  const url = useRouteUrl();
  const { pathname } = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);
  const { tabs, morePages } = getMobileNavTabs(subPages);

  const getPath = (page: SubPageModel) => `${url}${page.url}`;
  const isActive = (page: SubPageModel) =>
    !page.shouldOpenDialog &&
    !!matchPath({ path: getPath(page), end: !page.url }, pathname);

  const activeTab = morePages.some(isActive)
    ? MORE_TAB
    : tabs.find(isActive)?.url ?? false;

  return (
    <>
      <Nav aria-label={t('app.subPages.menu.ariaLabel')}>
        <BottomNavigation showLabels value={activeTab}>
          {tabs.map((page) => (
            <Tab
              key={page.url}
              value={page.url}
              label={page.linkLabel}
              icon={page.linkIcon}
              {...(page.shouldOpenDialog
                ? { onClick: page.handleDialogOpen }
                : { component: NavLink, to: getPath(page) })}
            />
          ))}
          {morePages.length > 0 && (
            <Tab
              value={MORE_TAB}
              label={t('app.subPages.menu.more')}
              icon={<MoreHorizIcon />}
              aria-haspopup="dialog"
              onClick={() => setIsMoreOpen(true)}
            />
          )}
        </BottomNavigation>
      </Nav>

      <MoreSheet
        anchor="bottom"
        open={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
      >
        <List>
          {morePages.map((page) => (
            <li key={page.url}>
              <ListItemButton
                selected={isActive(page)}
                onClick={() => {
                  setIsMoreOpen(false);
                  page.handleDialogOpen?.();
                }}
                {...(page.shouldOpenDialog
                  ? {}
                  : { component: NavLink, to: getPath(page) })}
              >
                <MoreIcon>{page.linkIcon}</MoreIcon>
                <ListItemText>{page.linkLabel}</ListItemText>
              </ListItemButton>
            </li>
          ))}
        </List>
      </MoreSheet>
    </>
  );
};

export default OvMobileNav;

const Nav = styled.nav`
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  background: ${Colours.WHITE};

  .MuiBottomNavigation-root {
    height: ${Variables.layout.MOBILE_NAV_HEIGHT};
  }

  @media (min-width: ${breakpoints.lg}) {
    display: none;
  }
`;

const Tab = styled(BottomNavigationAction)<{
  component?: unknown;
  to?: string;
}>`
  && {
    flex: 1 1 0;
    min-width: 0;
    padding: 0.25rem 0.125rem;
    color: ${Colours.OV_CHARCOAL};

    &.Mui-selected {
      color: ${Colours.OV_BASE};
      font-weight: bold;
    }

    .MuiBottomNavigationAction-label,
    .MuiBottomNavigationAction-label.Mui-selected {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: ${Variables.fontSizes.SMALL};
    }
  }
`;

const MoreSheet = styled(Drawer)`
  .MuiDrawer-paper {
    max-height: 70%;
    border-radius: ${Variables.borderRadius.LARGE}
      ${Variables.borderRadius.LARGE} 0 0;
  }

  a {
    color: ${Colours.OV_BASE};
    text-decoration: none;
  }

  .MuiListItemButton-root {
    min-height: 3rem;
  }
`;

const MoreIcon = styled(ListItemIcon)`
  && {
    min-width: 2.25rem;
    color: ${Colours.OV_BASE};
  }
`;
